import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { EntityKeys, QueryType, queryClient } from 'utils/reactQuery'
import { useCallback, useEffect, useRef, useState } from 'react'

import axios from 'axios'
import { logger } from 'utils/logging'
// Leaf import — the barrel drags in the modal's whole render graph
import { parseFramableUrl } from 'components/common/ChargebeeModal/chargebeeUrl'
import { useGetPortalLink } from 'dataQueries/subscriptions.query'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'

/** The legacy Workspaces profile page emits the same click event */
const ANALYTICS_SOURCE = 'workspace_plan'

/** Origin + path only — the query and fragment must not travel to Chargebee */
function getPortalReturnUrl() {
  return `${window.location.origin}${window.location.pathname}`
}

export function useBillingPortal(workspaceId: string) {
  const { t } = useTranslation(['workspace_settings'])
  const { spawnErrorToast } = useSnackbar()

  /**
   * An id, not a boolean: the query swaps cache entries synchronously on a workspace change, so
   * a flag fixed up later in an effect leaves a frame armed against the other workspace's link.
   * And a request is required at all because a cached link would otherwise open the modal itself.
   */
  const [requestedWorkspaceId, setRequestedWorkspaceId] = useState<string | null>(null)
  const isPortalRequested = requestedWorkspaceId === workspaceId

  // The portal is multi-page, so its iframe fires `load` per in-portal navigation
  const hasReportedOpenRef = useRef(false)

  const portalLink = useGetPortalLink(workspaceId, getPortalReturnUrl())

  const resetPortalLink = useCallback(() => {
    queryClient.resetQueries({ queryKey: [EntityKeys.PORTAL_LINK, QueryType.GET, { workspaceId }] })
  }, [workspaceId])

  const failBillingPortal = useCallback((message: string, error: unknown, metadata: Record<string, unknown>) => {
    logger.error(message, error, {
      context: { component: 'PlanSummaryCard', action: 'getPortalLink', metadata: { workspaceId, ...metadata } },
    })
    spawnErrorToast(t('workspace_settings:plan.billing_error'))

    setRequestedWorkspaceId(null)
    hasReportedOpenRef.current = false
    resetPortalLink()
  }, [workspaceId, spawnErrorToast, t, resetPortalLink])

  // The link grants access to billing, so it must not outlive the screen that asked for it.
  // Also drops an error that landed after unmount, which would replay as a toast on remount.
  useEffect(() => () => resetPortalLink(), [resetPortalLink])

  useEffect(() => {
    if (!portalLink.isError) return

    // failBillingPortal clears the error — `enabled: false` means the next observer reads the
    // cached one instead of refetching, and would replay the toast on every remount
    failBillingPortal('Failed to fetch billing portal link', portalLink.error, {
      status: axios.isAxiosError(portalLink.error) ? portalLink.error.response?.status : undefined,
    })
  }, [portalLink.isError, portalLink.error, failBillingPortal])

  // A URL the modal refuses to frame is still a 200, so the error path above never runs
  useEffect(() => {
    if (!isPortalRequested || portalLink.isFetching || !portalLink.data) return
    if (parseFramableUrl(portalLink.data.url)) return

    failBillingPortal('Billing portal link is not a framable https URL', new Error('Unframable hosted page URL'), {
      scheme: portalLink.data.url?.split(':')[0],
    })
  }, [isPortalRequested, portalLink.isFetching, portalLink.data, failBillingPortal])

  const openBillingPortal = useCallback(() => {
    logAnalyticsEvent(AnalyticsEvent.WORKSPACE_PROFILE_MANAGE_BILLING, { planId: workspaceId, source: ANALYTICS_SOURCE })
    setRequestedWorkspaceId(workspaceId)
    hasReportedOpenRef.current = false
    portalLink.refetch()
  }, [portalLink, workspaceId])

  const closeBillingPortal = useCallback(() => {
    setRequestedWorkspaceId(null)
    hasReportedOpenRef.current = false
    resetPortalLink()
  }, [resetPortalLink])

  // Denominator for the failure rate. On iframe load, not link arrival — a link that never
  // renders is not an open, and counting it would hide the failure above.
  const onBillingPortalOpened = useCallback(() => {
    if (hasReportedOpenRef.current) return

    hasReportedOpenRef.current = true
    logAnalyticsEvent(AnalyticsEvent.WORKSPACE_PROFILE_BILLING_PORTAL_OPENED, { planId: workspaceId, source: ANALYTICS_SOURCE })
  }, [workspaceId])

  return {
    isFetchingPortalLink: portalLink.isFetching,
    // `isFetching`: RQ keeps the old `data` during a refetch, and that URL is already consumed
    isBillingPortalOpen: isPortalRequested && !portalLink.isFetching && !!portalLink.data,
    hostedPageUrl: portalLink.data?.url,
    openBillingPortal,
    closeBillingPortal,
    onBillingPortalOpened,
  }
}
